<script lang="ts" setup>
import { computed, onMounted, ref, reactive, watch } from "vue";
import BaseButton from "@/components/base/BaseButton.vue";
import type { CreatePaymentType } from "@/struct/PaymentStruct";
import BaseModal from "@/components/base/BaseModal.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import { setLocalStorageParams, getLocalStorageParams } from "@/utils/common";
import { useRouter } from "vue-router";
import { InputNumber } from "primevue";
import BaseFigureForIcon from "@/components/base/BaseFigureForIcon.vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const router = useRouter();
const store = useStore();

const symbolPosition = ref<string>("60px");
const inputValue = ref<number>(0);
const submitBtn = ref<HTMLButtonElement | null>(null);

const emailInputValue = ref<string>(
  getLocalStorageParams("paymentEmail") || ""
);
const isEmailError = ref<boolean>(false);
const isValidEmail = ref<boolean>(false);
const currentMsisdn = ref<string>("");
const isLoading = ref<boolean>(false);
const activePaymentOption = ref<"card" | "sber">("card");

//modal states & methods
interface ModalStates {
  [key: string]: boolean;
}

const modalStates = reactive<ModalStates>({
  errorModal: false,
  paymentModal: false,
  // Add as many modals as you need
});

const toggleModal = (state: boolean, name: string): void => {
  if (name in modalStates) {
    modalStates[name] = state;
  } else {
    console.warn(`Modal "${name}" does not exist.`);
  }
};

//price input methods
const presetPrices = ref<{ amount: number; active: boolean }[]>([
  {
    amount: 50,
    active: false,
  },
  {
    amount: 100,
    active: false,
  },
  {
    amount: 200,
    active: false,
  },
  {
    amount: 300,
    active: false,
  },
  {
    amount: 500,
    active: false,
  },
]);
const currentUserPhone = computed<number>(
  () => store.getters["tariff/currentUserPhone"]
);

const pickPresetPrice = (id: number) => {
  presetPrices.value.forEach((item) => {
    item.active = false;
  });
  let item = presetPrices.value[id];
  item.active = true;
  inputValue.value = item.amount;
  if (item.amount < 100) {
    symbolPosition.value = "90px";
  } else if (item.amount === 100) {
    symbolPosition.value = "110px";
  } else {
    symbolPosition.value = "120px";
  }
};

const isCorrectValue = computed(() => {
  return inputValue.value >= 10 && inputValue.value <= 15000;
});

//email input methods

const validateEmail = (isValid: boolean): void => {
  console.log(isValid);
  isEmailError.value = !isValid;
  isValidEmail.value = emailInputValue.value && isValid;
};

const toPayment = async () => {
  const el = submitBtn.value;
  if (el && (el as any).$el instanceof HTMLButtonElement) {
    (el as any).$el.blur();
  }
  isLoading.value = true;
  setLocalStorageParams("paymentEmail", emailInputValue.value);
  if (activePaymentOption.value === "sber") {
    window.open("https://sberbank.com/sms/mybill?cs=2116856134125", "_self");
    toggleModal(false, "paymentModal");
    isLoading.value = false;
  } else {
    const payload: CreatePaymentType = {
      msisdn: currentMsisdn.value,
      email: emailInputValue.value,
      amount: inputValue.value,
    };
    store
      .dispatch("customer/toPaymentCart", payload)
      .then((date) => {
        if (date?.REF) {
          window.open(date.REF, "_self");
        }
      })
      .finally(() => {
        isLoading.value = false;
        toggleModal(false, "paymentModal");
      });
  }
};

const keepNumbersAndLeadingPlus = (input: string) => {
  // Remove everything except digits and pluses
  let cleaned = input.replace(/[^0-9+]/g, "");
  // If there’s a plus not at the start, remove it
  cleaned = cleaned.replace(/(?!^)\+/g, "");
  // If there are multiple pluses at the start, keep only one
  cleaned = cleaned.replace(/^\++/, "+");
  return cleaned;
};

const goBack = () => {
  router.back();
};

const tabClasses = (active: boolean) => {
  return [active ? "active" : ""];
};

onMounted(() => {
  if (getLocalStorageParams("paymentMethod")) {
    activePaymentOption.value = getLocalStorageParams("paymentMethod") as
      | "card"
      | "sber";
  }
  currentMsisdn.value = keepNumbersAndLeadingPlus(
    String(currentUserPhone.value)
  );
});
</script>

<template>
  <div class="payment">
    <div class="payment-header">
      <div class="payment-header--back" @click="goBack">
        <BaseFigureForIcon
          size="medium"
          color="white"
          icon="arrow"
          position="left"
          is-button
        />
      </div>
      <div class="payment-header--title">
        <p class="caption-medium bold">{{ t("payment.page.title") }}</p>
      </div>
    </div>
    <div class="payment-body">
      <div class="payment-body--input payment-input">
        <InputNumber
          v-model="inputValue"
          class="payment-input"
          inputId="payment-input"
          suffix="₽"
          placeholder="0₽"
          fluid
        />
      </div>
      <div
        v-if="inputValue === 0 || inputValue == null"
        class="payment-body--tabs"
      >
        <div
          v-for="(item, index) in presetPrices"
          :key="index"
          class="payment-body--tabs-item"
          :class="tabClasses(item.active)"
          @click="pickPresetPrice(index)"
        >
          <p class="caption-medium" :class="item.active ? 'color-white' : ''">
            {{ item.amount }}₽
          </p>
        </div>
      </div>
      <div v-else class="payment-body--note">
        <p class="caption-medium color-text-secondary">
          {{ t("payment.page.amountHint") }}
        </p>
      </div>
      <div class="payment-body--email">
        <div class="payment-body--email_title">
          <p class="h3">{{ t("payment.page.emailTitle") }}</p>
        </div>
        <div class="payment-body--email_subtitle">
          <p class="caption-select">{{ t("payment.page.emailSubtitle") }}</p>
        </div>
        <div class="payment-body--email_input">
          <BaseInput
            v-model="emailInputValue"
            @focus="isValidEmail = false"
            @validation="validateEmail"
            :has-error="isEmailError"
            type="email"
            :placeholder="t('payment.page.emailPlaceholder')"
          />
        </div>
        <div v-if="isEmailError" class="payment-body--email_error">
          <p class="caption-small color-error">
            {{ t("payment.page.emailError") }}
          </p>
        </div>
      </div>
      <div class="payment-body--btn">
        <BaseButton
          ref="submitBtn"
          :title="t('payment.page.topUp')"
          :disabled="
            !isCorrectValue ||
            isLoading ||
            isEmailError ||
            !emailInputValue ||
            !isValidEmail
          "
          @click="toPayment"
        />
      </div>
    </div>
  </div>
  <Teleport to=".root">
    <BaseModal
      v-if="modalStates.errorModal"
      :title="t('common.errors.title')"
      :main-btn-title="t('payment.page.errorClose')"
      @close="toggleModal(false, 'errorModal')"
      @action="toggleModal(false, 'errorModal')"
    />
  </Teleport>
</template>

<style lang="scss" scoped>
@import "@/assets/style/components/payment";
</style>
